// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// 3rd party imports
import notify from 'devextreme/ui/notify';
import { DxFormComponent } from 'devextreme-angular';

// Service imports
import { ScreenService } from '../../_services/shared-services/screen.service';
import { CiraHelpService } from '../../_services/shared-services/cira-help.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { CountryStateService } from '../../_services/shared-services/country-state.service';
import { ContextManagerService } from '../../_services/data-services/context-manager.service';
import { LogReturnedMailService } from '../_services/log-returned-mail.service';

// Constant imports
import { IconConstants } from 'cc-common';
import { MessageInterval } from '../../_constants/messages-intervals';
import { CiraHelpKeyConstants } from '../../_constants/cira-help-key-constants';

// Model imports
import { AddressRawDto } from '../../_models/address-raw-dto';
import { StateResultDto } from '../../_models/state-result-dto';
import { CountryResultDto } from '../../_models/country-result-dto';
import { GetStatesParameterDto } from '../../_models/get-states-parameter-dto';
import { PropertyCommunityInfoResultDto } from '../../_models/property-community-info-result-dto';
import { OwnerLogReturnedMailEditorResultDto } from '../_models/log-returned-mail/owner-log-returned-mail-editor-result-dto';
import { OwnerLogReturnedMailParametersDto } from '../_models/log-returned-mail/owner-log-returned-mail-parameters-dto';

@Component({
    selector: 'app-log-returned-mail',
    templateUrl: './log-returned-mail.component.html',
    styleUrls: ['./log-returned-mail.component.scss']
})
export class LogReturnedMailComponent implements OnInit {
    @ViewChild('editorForm', { static: false }) editorForm: DxFormComponent;

    @Input() visible = false;
    @Output() onClose = new EventEmitter<boolean>();

    pageTitle = 'Log Returned Mail';
    loadingVisible = false;
    isSmallScreen = false;

    iconCancel = IconConstants.CANCEL;
    iconSave = IconConstants.SAVE;

    countries: CountryResultDto[];
    states: StateResultDto[];
    mailingAddress: AddressRawDto;
    selectedContext: PropertyCommunityInfoResultDto;
    editorResult: OwnerLogReturnedMailEditorResultDto;
    recentResult: OwnerLogReturnedMailEditorResultDto;
    editorParams: OwnerLogReturnedMailParametersDto;
    helpLink: string;

    constructor(
        private readonly screenService: ScreenService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly errorService: ServerErrorService,
        private readonly countryStateService: CountryStateService,
        private readonly contextManagerService: ContextManagerService,
        private readonly logReturnedMailService: LogReturnedMailService
    ) {
        this.onCountrySelected = this.onCountrySelected.bind(this);
        this.onStateSelected = this.onStateSelected.bind(this);
        this.onAddressMatchesChange = this.onAddressMatchesChange.bind(this);
    }

    ngOnInit(): void {
        this.mailingAddress = new AddressRawDto();
        this.editorParams = new OwnerLogReturnedMailParametersDto();
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.logReturnedMail);
        this.updateScreenSize();
        this.selectedContext = this.contextManagerService.getSelectedContext();
        if (this.selectedContext && this.selectedContext.communityId) {
            this.populateLogReturnedMail(this.selectedContext.communityId, this.selectedContext.customerId);
        }
    }

    onShown(e: any): void {
        e.component.repaint();
    }

    onHidden(e: any): void {
        this.loadingVisible = false;
        this.editorResult = null;
        this.onClose.emit(false);
    }

    onCountrySelected(e): void {
        if (!!e.value && !!e.value.length) {
            this.mailingAddress.country = this.countries.find((c) => c.code === e.value).country;
            this.populateStates(e.value);
        }
    }

    onStateSelected(e): void {
        if (!!e.value && !!e.value.length) {
            this.mailingAddress.state = this.states.find((s) => s.code === this.mailingAddress.stateCode).state;
        }
    }

    onAddressMatchesChange(e: any): void {
        this.editorParams.updateMailingAddress = e.selectedItem.key !== 'Yes';
    }

    onCancelClick(): void {
        this.visible = false;
    }

    onSaveClick(e: any): void {
        const validate = e.validationGroup.validate();
        if (validate.isValid) {
            this.editorParams.communityId = this.selectedContext.communityId;
            this.editorParams.customerId = this.selectedContext.customerId;
            if (this.editorParams.updateMailingAddress) {
                this.editorParams.mailingAddress = this.mailingAddress;
            }
            this.loadingVisible = true;
            this.logReturnedMailService.saveLogReturnedMail(this.editorParams).subscribe(
                () => {
                    notify('Returned Mail Entry Logged Successfully', 'success', MessageInterval.successMsgInterval);
                    this.loadingVisible = false;
                    this.visible = false;
                },
                (error) => {
                    this.errorService.displayError(error);
                    this.loadingVisible = false;
                }
            );
        }
    }

    private populateLogReturnedMail(communityId: number, customerId?: string): void {
        this.loadingVisible = true;
        this.logReturnedMailService.getLogReturnedMailEditor(communityId, customerId).subscribe(
            (result) => {
                this.editorResult = result;
                this.loadingVisible = false;
                this.visible = true;
                this.populateCountries();
            },
            (error) => {
                this.errorService.displayError(error);
                this.loadingVisible = false;
            }
        );
    }

    private populateCountries(): void {
        this.countryStateService.getCountries().subscribe((countries) => {
            this.countries = countries;
            this.mailingAddress.countryCode = (
                countries.find((c) => c.country === this.mailingAddress.country) || { code: 'US' }
            ).code;
            this.populateStates(this.mailingAddress.countryCode);
        });
    }

    private populateStates(countryCode: string): void {
        const param: GetStatesParameterDto = new GetStatesParameterDto(countryCode);
        this.countryStateService.getStates(param).subscribe((states) => {
            this.states = states;
            if (!this.mailingAddress.stateCode) {
                this.mailingAddress.stateCode = (
                    states.find((s) => s.state === this.mailingAddress.state) || { code: null }
                ).code;
            } else {
                this.mailingAddress.state = (
                    this.states.find((s) => s.code === this.mailingAddress.stateCode) || { state: null }
                ).state;
            }
        });
    }

    private updateScreenSize(): void {
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.screenService.changed.subscribe(() => (this.isSmallScreen = this.screenService.isSmallScreen()));
    }
}
